<template>
    <form-wrapper class="intake-history" @submit="handleFormSubmission(data)">
        <intake-form-section :title="$t('forms.intake.medical_surgical.sections.past_medical')">
            <form-row>
                <form-cell>
                    <tabs-field
                        :label="$t('forms.intake.medical_surgical.fields.medical_conditions.label')"
                        :options="medicalConditionOptions"
                        :formData="data"
                        valuePath="medicalConditionsStatus"
                        size="small"
                        @change="change($event)"
                    ></tabs-field>
                </form-cell>
            </form-row>
        </intake-form-section>

        <intake-form-section :title="$t('forms.intake.medical_surgical.sections.past_surgical')">
            <p class="intake-history__prompt">
                {{ $t('forms.intake.medical_surgical.fields.surgeries.prompt') }}
            </p>

            <p v-if="!data.surgeries.length" class="intake-history__empty">
                {{ $t('forms.intake.medical_surgical.empty') }}
            </p>

            <div v-for="(surgery, index) in data.surgeries" :key="`surgery-${index}`" class="intake-history__card">
                <div class="intake-history__card-header">
                    <h4 class="intake-history__card-title">
                        {{ $t('forms.intake.medical_surgical.surgery_label', { number: index + 1 }) }}
                    </h4>
                    <common-button
                        :label="$t('common.microcopy.remove')"
                        type="secondary"
                        size="small"
                        :isSubmittable="false"
                        @click="removeSurgery(index)"
                    ></common-button>
                </div>

                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.intake.medical_surgical.fields.procedure.label')"
                            :placeholder="$t('forms.intake.medical_surgical.fields.procedure.placeholder')"
                            :isRequired="true"
                            :formData="data"
                            :valuePath="`surgeries.${index}.procedure`"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.intake.medical_surgical.fields.body_region.label')"
                            :placeholder="$t('forms.intake.medical_surgical.fields.body_region.placeholder')"
                            :formData="data"
                            :valuePath="`surgeries.${index}.bodyRegion`"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.intake.medical_surgical.fields.date_or_year.label')"
                            :placeholder="$t('forms.intake.medical_surgical.fields.date_or_year.placeholder')"
                            :formData="data"
                            :valuePath="`surgeries.${index}.dateOrYear`"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.intake.medical_surgical.fields.surgeon.label')"
                            :placeholder="$t('forms.intake.medical_surgical.fields.surgeon.placeholder')"
                            :formData="data"
                            :valuePath="`surgeries.${index}.surgeon`"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.intake.medical_surgical.fields.facility.label')"
                            :placeholder="$t('forms.intake.medical_surgical.fields.facility.placeholder')"
                            :formData="data"
                            :valuePath="`surgeries.${index}.facility`"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.intake.medical_surgical.fields.reason.label')"
                            :placeholder="$t('forms.intake.medical_surgical.fields.reason.placeholder')"
                            :formData="data"
                            :valuePath="`surgeries.${index}.reason`"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <select-field
                            :label="$t('forms.intake.medical_surgical.fields.timing.label')"
                            :placeholder="$t('forms.intake.medical_surgical.fields.timing.placeholder')"
                            :options="timingOptions"
                            :formData="data"
                            :valuePath="`surgeries.${index}.timing`"
                            @change="change($event)"
                        ></select-field>
                    </form-cell>
                </form-row>
            </div>

            <button class="intake-history__add" type="button" @click="addSurgery">
                + {{ $t('forms.intake.medical_surgical.actions.add_surgery') }}
            </button>
        </intake-form-section>

        <template #footer>
            <common-buttons>
                <common-button
                    :label="$t('common.microcopy.back')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="goBack"
                ></common-button>
                <common-button
                    :label="$t('common.microcopy.next')"
                    type="primary"
                    size="large"
                    :isProcessing="isSending"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import IntakeFormSection from 'components/forms/intake/IntakeFormSection'

    const MEDICAL_CONDITION_STATUSES = ['none_reported', 'has_conditions', 'unknown']
    const TIMING_OPTIONS = ['pre_existing', 'post_index_event']

    export default {
        mixins: [FormMixin],

        components: {
            IntakeFormSection,
        },

        props: {
            initialData: { type: Object, default: undefined },
        },

        data() {
            return {
                data: {
                    medicalConditionsStatus: undefined,
                    surgeries: [],
                    ...(this.initialData || {}),
                },
            }
        },

        computed: {
            medicalConditionOptions() {
                return MEDICAL_CONDITION_STATUSES.map((value) => ({
                    label: this.$t(`forms.intake.medical_surgical.fields.medical_conditions.options.${value}`),
                    value,
                }))
            },

            timingOptions() {
                return TIMING_OPTIONS.map((value) => ({
                    label: this.$t(`forms.intake.medical_surgical.fields.timing.options.${value}`),
                    value,
                }))
            },
        },

        methods: {
            createSurgery() {
                return {
                    procedure: undefined,
                    bodyRegion: undefined,
                    dateOrYear: undefined,
                    surgeon: undefined,
                    facility: undefined,
                    reason: undefined,
                    timing: 'pre_existing',
                }
            },

            addSurgery() {
                this.change({
                    path: 'surgeries',
                    value: [...this.data.surgeries, this.createSurgery()],
                })
            },

            removeSurgery(index) {
                const next = [...this.data.surgeries]
                next.splice(index, 1)
                this.change({ path: 'surgeries', value: next })
            },

            goBack() {
                this.emitEvent({ name: 'back', payload: this.data })
            },

            handleFormSubmission(data) {
                this.isSending = true
                this.emitSent(data)
                this.isSending = false
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';

    .intake-history__prompt {
        margin: 0 0 rem(12px);
        font-size: rem($font-size--form);
        color: $color--txt-base;
    }

    .intake-history__empty {
        margin: 0 0 rem(12px);
        font-size: rem($font-size--form);
        color: $color--txt-minor;
    }

    .intake-history__card {
        margin-bottom: rem(16px);
        padding: rem(16px);
        border: rem(1px) solid $color--border-base;
        border-radius: rem($border-radius--base);
        background: $color--bg-dropdown;
    }

    .intake-history__card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: rem(12px);
        margin-bottom: rem(12px);
        flex-wrap: wrap;
    }

    .intake-history__card-title {
        margin: 0;
        font-size: rem($font-size--form);
        font-weight: 600;
        color: $color--txt-header;
    }

    .intake-history__add {
        display: block;
        width: 100%;
        margin-top: rem(4px);
        padding: rem(14px) rem(12px);
        border: rem(1px) dashed $color--accent-1;
        border-radius: rem($border-radius--base);
        background: transparent;
        color: $color--accent-1;
        font-size: rem($font-size--form);
        font-weight: 600;
        text-align: center;
        cursor: pointer;

        &:hover {
            background: rgba($color--accent-1, 0.06);
        }
    }
</style>
